// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Construction, Pencil, Plus, Power, PowerOff, RotateCcw, Search, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TimeValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type DateFormatPreference, formatDateLong } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

export interface ActivityEntry {
  id: number;
  timestamp: string;
  user: string;
  action: "created" | "updated" | "deleted" | "restored" | "activated" | "deactivated";
  target: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  message?: string;
  /** Optional tag for consumer-side filtering (e.g. "chain" vs "store") */
  tag?: string;
}

const ACTION_ICONS: Record<ActivityEntry["action"], { icon: React.FC<{ className?: string }>; destructive?: boolean }> =
  {
    created: { icon: Plus },
    updated: { icon: Pencil },
    deleted: { icon: Trash2, destructive: true },
    restored: { icon: RotateCcw },
    activated: { icon: Power },
    deactivated: { icon: PowerOff, destructive: true },
  };

const TIME_FILTER_OPTIONS = [
  { label: "Last 1 day", value: "1" },
  { label: "Last 10 days", value: "10" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
];

function ValuePill({ children, variant }: { children: React.ReactNode; variant?: "old" | "new" | "neutral" }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium",
        variant === "old" &&
          "bg-red-100 text-red-700 border border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800",
        variant === "new" &&
          "bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800",
        (!variant || variant === "neutral") && "bg-muted text-foreground border border-border",
      )}
    >
      {children}
    </span>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// MOCK: gravatar URLs for known users
const MOCK_AVATARS: Record<string, string> = {
  "lukasz@focal.systems": "https://gravatar.com/avatar/e8e14b38e828e82daf82686b41b29e04?s=32",
};

function UserBadge({ user }: { user: string }) {
  const name = user.split("@")[0] ?? user;
  const avatarUrl = MOCK_AVATARS[user];
  return (
    <strong className="inline-flex items-baseline gap-1.5">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="inline h-4 w-4 rounded-full border border-border object-cover translate-y-[3px]"
        />
      ) : (
        <span className="inline-flex items-center justify-center h-4 w-4 rounded-full border border-border bg-muted text-[8px] font-semibold leading-none text-muted-foreground -translate-y-[1px]">
          {getInitials(name)}
        </span>
      )}
      {name}
    </strong>
  );
}

function EntryDescription({ entry }: { entry: ActivityEntry }) {
  switch (entry.action) {
    case "created":
      return (
        <span>
          <UserBadge user={entry.user} /> created <ValuePill variant="neutral">{entry.target}</ValuePill>
        </span>
      );
    case "activated":
      return (
        <span>
          <UserBadge user={entry.user} /> activated <ValuePill variant="neutral">{entry.target}</ValuePill>
        </span>
      );
    case "deactivated":
      return (
        <span>
          <UserBadge user={entry.user} /> deactivated <ValuePill variant="neutral">{entry.target}</ValuePill>
        </span>
      );
    case "deleted":
      return (
        <span>
          <UserBadge user={entry.user} /> deleted{" "}
          {entry.field ? (
            <>
              <ValuePill variant="old">{entry.field}</ValuePill> from{" "}
              <ValuePill variant="neutral">{entry.target}</ValuePill>
            </>
          ) : (
            <ValuePill variant="old">{entry.target}</ValuePill>
          )}
        </span>
      );
    case "restored":
      return (
        <span>
          <UserBadge user={entry.user} /> restored{" "}
          {entry.field ? (
            <>
              <ValuePill variant="neutral">{entry.field}</ValuePill> in{" "}
              <ValuePill variant="neutral">{entry.target}</ValuePill>
            </>
          ) : (
            <ValuePill variant="neutral">{entry.target}</ValuePill>
          )}{" "}
          to default
        </span>
      );
    case "updated":
      return (
        <span>
          <UserBadge user={entry.user} /> change {entry.field ?? entry.target}
          {entry.field && (
            <>
              {" "}
              in <ValuePill variant="neutral">{entry.target}</ValuePill>
            </>
          )}
          {entry.oldValue && entry.newValue && (
            <>
              {" "}
              from <ValuePill variant="old">{entry.oldValue}</ValuePill> to{" "}
              <ValuePill variant="new">{entry.newValue}</ValuePill>
            </>
          )}
        </span>
      );
  }
}

function getDateGroup(timestamp: string, dateFormat: DateFormatPreference): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (entryDate.getTime() === today.getTime()) return "TODAY";
  if (entryDate.getTime() === yesterday.getTime()) return "YESTERDAY";
  return formatDateLong(entryDate, dateFormat).toUpperCase();
}

export const ACTIVITY_FILTER_DEFAULTS = {
  search: "",
  timeRange: "30",
};

export type ActivityFilterState = typeof ACTIVITY_FILTER_DEFAULTS;

interface ActivityTimelineProps {
  entries: ActivityEntry[];
  /** Extra filter controls rendered after the time range filter */
  filters?: React.ReactNode;
  /** Whether extra (consumer-managed) filters are non-default — used to show the clear button */
  hasActiveFilters?: boolean;
  /** Called when the clear button is clicked — consumer should reset its own extra filters */
  onReset?: () => void;
  /** Initial filter values (typically decoded from URL `f` param by the consumer) */
  initialFilters?: Partial<ActivityFilterState>;
  /** Called when filters change with raw values — consumer is responsible for encoding to URL */
  onFilterChange?: (filters: ActivityFilterState) => void;
}

export function ActivityTimeline({
  entries,
  filters,
  hasActiveFilters,
  onReset,
  initialFilters,
  onFilterChange,
}: ActivityTimelineProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const [search, setSearch] = useState(initialFilters?.search ?? ACTIVITY_FILTER_DEFAULTS.search);
  const [timeRange, setTimeRange] = useState(initialFilters?.timeRange ?? ACTIVITY_FILTER_DEFAULTS.timeRange);

  const syncFilters = useCallback(
    (overrides: Partial<ActivityFilterState> = {}) => {
      onFilterChange?.({ search, timeRange, ...overrides });
    },
    [search, timeRange, onFilterChange],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncFilters();
    },
    [syncFilters],
  );

  const filtered = useMemo(() => {
    const cutoff = Date.now() - Number(timeRange) * 86_400_000;
    let result = entries.filter((e) => new Date(e.timestamp).getTime() >= cutoff);

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.user.toLowerCase().includes(q) ||
          e.target.toLowerCase().includes(q) ||
          e.action.toLowerCase().includes(q) ||
          e.field?.toLowerCase().includes(q) ||
          e.oldValue?.toLowerCase().includes(q) ||
          e.newValue?.toLowerCase().includes(q) ||
          e.message?.toLowerCase().includes(q),
      );
    }

    return result;
  }, [entries, search, timeRange]);

  const grouped = useMemo(() => {
    const groups: { label: string; entries: ActivityEntry[] }[] = [];
    for (const entry of filtered) {
      const label = getDateGroup(entry.timestamp, dateFormat);
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.entries.push(entry);
      } else {
        groups.push({ label, entries: [entry] });
      }
    }
    return groups;
    // `dateFormat` is a real dependency: the group labels are formatted dates, so they have to
    // be rebuilt when the viewer changes their date preference.
  }, [filtered, dateFormat]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-2 px-4 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs">
        <Construction className="h-3.5 w-3.5 shrink-0" />
        {t("activity.wipBanner")}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("activity.searchPlaceholder")}
            className="pl-9"
          />
        </div>
        <FilterSelect
          label={t("activity.filter.timeRange")}
          value={timeRange}
          onChange={setTimeRange}
          options={TIME_FILTER_OPTIONS}
          searchPlaceholder="Search..."
          emptyText="No options"
          hideClear
        />
        {filters}
        {(search || timeRange !== ACTIVITY_FILTER_DEFAULTS.timeRange || hasActiveFilters) && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setSearch(ACTIVITY_FILTER_DEFAULTS.search);
              setTimeRange(ACTIVITY_FILTER_DEFAULTS.timeRange);
              onReset?.();
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {grouped.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">{t("activity.empty")}</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.label}>
              <div className="mb-3 text-xs font-semibold tracking-wider text-muted-foreground">{group.label}</div>
              <div className="space-y-0">
                {group.entries.map((entry) => (
                  <div key={entry.id} className="group flex gap-4 py-2.5 border-b border-border/50 last:border-b-0">
                    <div className="w-14 shrink-0 pt-0.5 text-xs text-muted-foreground">
                      <TimeValue value={entry.timestamp} />
                    </div>
                    <div className="pt-1 shrink-0">
                      {(() => {
                        const { icon: Icon, destructive } = ACTION_ICONS[entry.action];
                        return (
                          <Icon className={cn("h-3.5 w-3.5", destructive ? "text-destructive" : "text-primary")} />
                        );
                      })()}
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="text-sm leading-relaxed [&_strong]:font-semibold [&_strong]:text-foreground">
                        <EntryDescription entry={entry} />
                      </div>
                      {entry.message && (
                        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
                          <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                            Message
                          </div>
                          {entry.message}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
